import React, { FC, Fragment, useContext, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import AddEditDepartmentDialog from './add-edit-department-dialog/add-edit-department-dialog.component';
import { useDepartments } from 'context/departments-context';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import { Department } from 'models/organization';
import { User } from 'models/user';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'text', width: 100 },
    { type: 'text', width: 100 },
    { type: 'button', width: 80 },
];

const Departments: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'workflow', 'common']);
    const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState<Department>();
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const { departments, departmentsLoading, deleteDepartment, reorderDepartments } = useDepartments();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const handleDialogClose = () => {
        setDepartmentDialogOpen(false);
    };

    const handleAddDepartmentClick = () => {
        setSelectedDepartment(undefined);
        setDepartmentDialogOpen(true);
    };

    const handleEditDepartmentClick = (department: Department) => {
        setSelectedDepartment(department);
        setDepartmentDialogOpen(true);
    };

    const handleDeleteDepartmentClick = (departmentId: string) => {
        setDeleteConfirmId(departmentId);
    };

    const handleConfirmDelete = async () => {
        if (!deleteConfirmId) return;
        const result = await deleteDepartment({ departmentId: deleteConfirmId });
        setDeleteConfirmId(null);
        if (result) {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:DEPARTMENT_DELETED_SUCCESSFULLY'),
            });
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination || result.source.index === result.destination.index) return;

        const reordered = Array.from(departments);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);

        const departmentIds = reordered.map((d) => d._id || '');
        await reorderDepartments({ departmentIds });
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('lab-settings:DEPARTMENTS_REORDERED_SUCCESSFULLY'),
        });
    };

    return (
        <Fragment>
            <Box mb={1}>
                <Button startIcon={<AddIcon />} onClick={() => handleAddDepartmentClick()}>
                    {translate('lab-settings:ADD_DEPARTMENT')}
                </Button>
            </Box>
            <Paper variant="outlined">
                <DragDropContext onDragEnd={handleDragEnd}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={40}></TableCell>
                                    <TableCell>{translate('lab-settings:DEPARTMENTS')}</TableCell>
                                    <TableCell>{translate('lab-settings:WORKFLOW_STEPS')}</TableCell>
                                    <TableCell>{translate('common:TECHNICIANS')}</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <Droppable droppableId="departments">
                                {(provided) => (
                                    <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                                        {departmentsLoading && (
                                            <SkeletonTable columns={skeletonColumns} rows={3} renderRowsOnly={true} />
                                        )}
                                        {departments?.length > 0 &&
                                            departments.map((department, index) => (
                                                <Draggable
                                                    key={department._id}
                                                    draggableId={department._id || `dept-${index}`}
                                                    index={index}
                                                >
                                                    {(provided) => (
                                                        <TableRow
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            hover
                                                            onClick={() => handleEditDepartmentClick(department)}
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            <TableCell
                                                                {...provided.dragHandleProps}
                                                                onClick={(e) => e.stopPropagation()}
                                                                sx={{ cursor: 'grab', width: 40 }}
                                                            >
                                                                <DragIndicatorIcon color="action" />
                                                            </TableCell>
                                                            <TableCell>{department.name}</TableCell>
                                                            <TableCell>
                                                                {department.workflowSteps
                                                                    ?.map((step) => step.name)
                                                                    .join(', ')}
                                                            </TableCell>
                                                            <TableCell>
                                                                {(department.technicians as User[])
                                                                    ?.map((tech) => tech.fullName)
                                                                    .join(', ')}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <IconButton
                                                                    disabled={departmentsLoading}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteDepartmentClick(
                                                                            department._id || '',
                                                                        );
                                                                    }}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </Draggable>
                                            ))}
                                        {provided.placeholder}
                                        {!departments?.length && !departmentsLoading && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    {translate('lab-settings:NO_DEPARTMENTS_FOUND')}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                )}
                            </Droppable>
                        </Table>
                    </TableContainer>
                </DragDropContext>
            </Paper>

            <AddEditDepartmentDialog
                department={selectedDepartment}
                open={departmentDialogOpen}
                onDialogClose={handleDialogClose}
            />

            <ConfirmDialog
                open={!!deleteConfirmId}
                handleClose={() => setDeleteConfirmId(null)}
                handleConfirm={handleConfirmDelete}
                dialogTitle={translate('common:DELETE')}
                dialogContent={translate('lab-settings:CONFIRM_DELETE_DEPARTMENT')}
                confirmBtnLabel={translate('common:DELETE')}
            />
        </Fragment>
    );
};

export default Departments;
